import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, Package, ShieldCheck, Users, FolderKanban, ListChecks, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import { toast } from '@/app/components/ui/simple-toaster';
import { usePermissions } from '@/hooks/use-permissions';
import { usePlatformModules } from '@/hooks/use-modules';
import { usePlans, useSubscription, useCreateCheckoutSession } from '@/hooks/use-billing';
import type { SubscriptionPlan, BillingCycle } from '@/lib/types';

interface ModuleInfo {
  key: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  features: string[];
}

const MODULE_CATALOG: ModuleInfo[] = [
  {
    key: 'safety',
    name: 'H3Safe',
    description: 'Safety management and compliance',
    icon: ShieldCheck,
    features: [
      'Project Safety Analyses (JSAs)',
      'Safe Work Plans',
      'Rescue Plans',
      'Toolbox Talks',
      'Near Miss Reporting',
      'Incident Reports',
      'Safety Diagrams',
    ],
  },
  {
    key: 'crew',
    name: 'H3Crew',
    description: 'Workforce and crew management',
    icon: Users,
    features: [
      'Employee Profiles & Certifications',
      'Crew Lists & Assignments',
      'Availability Tracking',
      'Travel Management',
      'Onboarding Checklists',
      'Performance Reviews',
    ],
  },
  {
    key: 'projects',
    name: 'H3Books',
    description: 'Billing & project finances',
    icon: FolderKanban,
    features: [
      'Project & Phase Management',
      'Task Tracking',
      'Client Management',
      'Budget Tracking',
      'Resource Allocation',
      'Project Timelines',
    ],
  },
  {
    key: 'work',
    name: 'H3Project',
    description: 'Work management and task checklists',
    icon: ListChecks,
    features: [
      'Project Task Checklists',
      'Daily Work Reports',
      'Delay & Progress Logging',
      'Crew Assignment Tracking',
      'Phase-based Work Planning',
      'Linked Billing Workflow',
    ],
  },
  {
    key: 'payroll',
    name: 'H3Pay',
    description: 'Payroll and time tracking',
    icon: DollarSign,
    features: [
      'Time Entry & Timesheets',
      'Payroll Processing',
      'Rate Management',
      'Overtime Calculations',
      'Pay Period Reports',
      'Export to Accounting',
    ],
  },
];

export function UpgradePage() {
  const navigate = useNavigate();
  const { canManageOrganization } = usePermissions();
  const { isModuleEnabled, isLoading: modulesLoading } = usePlatformModules();
  const { data: plans, isLoading: plansLoading } = usePlans();
  const { data: _subData } = useSubscription();
  const checkoutSession = useCreateCheckoutSession();
  const [selectedBillingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [subscribingPlanId, setSubscribingPlanId] = useState<number | null>(null);

  if (!canManageOrganization) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-foreground">Admin Access Required</h2>
            <p className="text-sm text-gray-500 dark:text-muted-foreground mt-1">
              Only administrators can add or manage modules.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isLoading = modulesLoading || plansLoading;

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-1/3" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  const unactivatedModules = MODULE_CATALOG.filter((m) => !isModuleEnabled(m.key));
  const activatedModules = MODULE_CATALOG.filter((m) => isModuleEnabled(m.key));

  // Find individual plans that cover each module
  const findPlanForModule = (moduleKey: string): SubscriptionPlan | undefined => {
    return (plans || []).find(
      (p) =>
        !p.is_bundle && !p.is_free && p.is_active && (p.modules_included || []).includes(moduleKey)
    );
  };

  const annualAvailable = unactivatedModules.some((m) => findPlanForModule(m.key)?.has_annual_price);
  const billingCycle: BillingCycle = annualAvailable ? selectedBillingCycle : 'monthly';
  // Monthly-only plans stay on monthly billing even in Annual mode.
  const cycleFor = (plan: SubscriptionPlan): BillingCycle =>
    billingCycle === 'annual' && plan.has_annual_price ? 'annual' : 'monthly';

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    setSubscribingPlanId(plan.id);
    try {
      const result = await checkoutSession.mutateAsync({
        plan_id: plan.id,
        billing_cycle: cycleFor(plan),
        success_url: `${window.location.origin}/settings/upgrade?checkout=success`,
        cancel_url: `${window.location.origin}/settings/upgrade`,
      });
      // eslint-disable-next-line react-hooks/immutability
      window.location.href = result.checkout_url;
    } catch {
      toast.error('Error', {
        description: 'Could not start checkout. Please contact support.',
      });
      setSubscribingPlanId(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-foreground">Add Modules</h1>
          <p className="text-gray-600 dark:text-muted-foreground mt-1">
            Expand H3ight with additional modules
          </p>
        </div>
      </div>

      {/* Billing Toggle: only when a plan on screen has an annual Stripe price */}
      {annualAvailable && (
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              billingCycle === 'monthly'
                ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-muted-foreground hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              billingCycle === 'annual'
                ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-muted-foreground hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            Annual
            <span className="ml-1.5 text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">
              Save 2 months
            </span>
          </button>
        </div>
      )}

      {/* Available Modules */}
      {unactivatedModules.length > 0 ? (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-foreground mb-4">Available Modules</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {unactivatedModules.map((mod) => {
              const plan = findPlanForModule(mod.key);
              const price = plan
                ? cycleFor(plan) === 'annual'
                  ? plan.price_annual
                  : plan.price_monthly
                : null;
              const Icon = mod.icon;

              return (
                <Card key={mod.key} className="flex flex-col">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                          <Icon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{mod.name}</CardTitle>
                          <p className="text-sm text-gray-500 dark:text-muted-foreground">{mod.description}</p>
                        </div>
                      </div>
                      {price && (
                        <div className="text-right flex-shrink-0">
                          <span className="text-2xl font-bold text-gray-900 dark:text-foreground">${price}</span>
                          <span className="text-gray-500 dark:text-muted-foreground text-xs block">
                            /{plan && cycleFor(plan) === 'annual' ? 'year' : 'month'}
                          </span>
                          {plan && billingCycle === 'annual' && !plan.has_annual_price && (
                            <span className="text-gray-500 dark:text-muted-foreground text-xs block">
                              Monthly only
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col justify-between gap-4">
                    <ul className="space-y-1.5">
                      {mod.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2 text-sm text-gray-700">
                          <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    {plan ? (
                      <Button
                        className="w-full"
                        disabled={subscribingPlanId === plan.id}
                        onClick={() => handleSubscribe(plan)}
                      >
                        {subscribingPlanId === plan.id ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Redirecting to checkout...
                          </>
                        ) : (
                          `Add ${mod.name}`
                        )}
                      </Button>
                    ) : (
                      <Button className="w-full" onClick={() => navigate('/settings/subscription/plans')}>
                        View Plans
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Check className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-foreground">All Modules Activated</h2>
            <p className="text-sm text-gray-500 dark:text-muted-foreground mt-1">
              Your organization has all available modules enabled.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Already Activated Modules */}
      {activatedModules.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-foreground mb-4">Active Modules</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {activatedModules.map((mod) => {
              const Icon = mod.icon;
              return (
                <Card key={mod.key} className="border-green-200 bg-green-50/30">
                  <CardContent className="py-4 flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-4 w-4 text-green-700" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-foreground text-sm">{mod.name}</p>
                      <p className="text-xs text-gray-500 dark:text-muted-foreground truncate">{mod.description}</p>
                    </div>
                    <Badge variant="outline" className="ml-auto flex-shrink-0 text-green-700 border-green-300 text-xs">
                      Active
                    </Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Bundle upsell */}
      {unactivatedModules.length >= 2 && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardContent className="py-6 text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-foreground">
              Need multiple modules? Save with a bundle!
            </h3>
            <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1 mb-4">
              Bundle packages include multiple modules at a discounted rate.
            </p>
            <Button variant="outline" onClick={() => navigate('/settings/subscription/plans')}>
              View Bundle Plans
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
